import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import formatPrice, { roundUpParser } from '@honda-canada/js-utilities/lib/formatPrice';
import { Box, H6, Optional, Wrapper as DsrWrapper } from '@honda-canada/design-system-react';
import { compiler } from 'markdown-to-jsx';
import AccessoryModalCta from './shared/AccessoryModalCta';
import AvailabilityStatusBadge from '../TrimCardGrid/shared/AvailabilityStatusBadge';
import PaymentCalculatorModal from '../PaymentCalculator/PaymentCalculatorModal';
import PaymentCompareModal from '../PaymentCompareModal';
import PaymentDetailsComponent from '../PaymentDetailsComponent';
import PriceBreakdownModal from '../PriceBreakdownModal';
import PriceComponent from '../PriceComponent';
import StandardCtas from './shared/StandardCtas';
import themeStyles from './SummaryBar.styles';
import { INVENTORY_VARIANT } from './utils/constants';
import { primaryCalculatorId } from '../../core/reducers/financialCalculators';
import { stripMarkdownHeading } from '../../utils/markdown';
import { variant2 } from '../PaymentDetailsComponent/utils/constants';
const Container = themeStyles.apply(Box, 'Container');
const Wrapper = themeStyles.apply(DsrWrapper, 'Wrapper');
const PriceSection = themeStyles.apply(Box, 'PriceSection');
const Finance = themeStyles.apply(Box, 'Finance');
const Price = themeStyles.apply(Box, 'Price');
const InventoryDetails = themeStyles.apply(Box, 'InventoryDetails');
const CtaSection = themeStyles.apply(Box, 'CtaSection');
const Badge = themeStyles.apply(AvailabilityStatusBadge, 'Badge');
const defaultPriceComponentStyles = {
    pricesContainer: {
        marginBottom: 0,
    },
    priceStyles: {
        title: {
            size: 'extraSmall',
            styles: {
                textWrap: 'nowrap',
            },
        },
        copy: {
            styles: {
                pt: 'zero',
                fontSize: ['16px', '16px', '18px'],
                lineHeight: ['20px', '20px', '22px'],
            },
        },
        tooltipContainer: {
            mt: ['-1px', '-1px', '1px'],
            mb: '1px',
            pt: ['zero', 'zero', '2px'],
            tooltip: {
                alignSelf: 'top',
            },
        },
        container: {
            mt: 'zero',
        },
    },
    errorStyles: {
        container: {
            height: 'auto',
            marginTop: 'zero',
        },
        copy: {
            styles: {
                width: 'auto',
                maxWidth: '700px',
            },
        },
    },
};
const priceFormatter = (price, language) => formatPrice(price, language, 0, roundUpParser);
const SummaryBarUI = ({ componentName, fields, variant, paymentMethod, paymentFrequency, paymentResponse, prices, priceErrorMessage, paymentTooltipParams, sellingPriceTooltipLabel, msrpTooltipLabel, overriddenIsSellingProvince, inventoryStatus, vehicleName, isSummaryPage, onCtaClickHandler, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { data, isFetching, error, isUninitialized } = paymentResponse || {};
    const apr = (_b = (_a = data === null || data === void 0 ? void 0 : data.terms) === null || _a === void 0 ? void 0 : _a.find(term => term.term === data.term)) === null || _b === void 0 ? void 0 : _b.apr;
    const hasCtaSection = ((_c = fields === null || fields === void 0 ? void 0 : fields.ctaLink1) === null || _c === void 0 ? void 0 : _c.href) || ((_d = fields === null || fields === void 0 ? void 0 : fields.ctaLink2) === null || _d === void 0 ? void 0 : _d.href);
    const priceComponentStyles = variant === INVENTORY_VARIANT
        ? {
            ...defaultPriceComponentStyles,
            priceStyles: {
                ...defaultPriceComponentStyles.priceStyles,
                container: {
                    ...defaultPriceComponentStyles.priceStyles.container,
                    height: 'auto',
                },
                tooltipContainer: {
                    ...defaultPriceComponentStyles.priceStyles.tooltipContainer,
                    mt: ['xxs', 'xxs', '2px'],
                    mb: ['xs', 'xxs', 'xxs'],
                },
            },
        }
        : defaultPriceComponentStyles;
    // Shared
    const priceComponent = (_jsx(PriceComponent, { allInPriceTooltipLabel: sellingPriceTooltipLabel, errorMessage: priceErrorMessage, hasError: !!error, height: ['auto', 'auto', undefined], horizontalAlignment: ['right', 'right', 'right'], isFetching: isFetching || isUninitialized, msrpStartingFromTooltipLabel: msrpTooltipLabel, priceComponentStyles: priceComponentStyles, priceFormatter: priceFormatter, prices: prices, alignItems: undefined, saveAboveLabel: undefined, suppressDiscount: undefined, showSellingPriceDisclaimer: undefined, frieghtPDIFeesCost: undefined, errorIconName: "exclamationAlert", overrideShowSellingPrice: overriddenIsSellingProvince, isLoadingIconLarge: true, loadingIconHeight: "28px", dealerFee: undefined, showRebateDisclaimerIndicator: false }));
    // Default sections
    const priceSection = (_jsxs(PriceSection, { isError: error, isFetching: isFetching, children: [_jsx(Finance, { isFetching: isFetching || isUninitialized, children: _jsx(Optional, { when: !error && !isFetching, children: _jsx(PaymentDetailsComponent, { componentName: "SummaryBar-PaymentDetails", variant: variant2, paymentLabel: {
                            method: paymentMethod,
                            frequency: paymentFrequency,
                        }, terms: data === null || data === void 0 ? void 0 : data.term, paymentAmount: data === null || data === void 0 ? void 0 : data.preferredPaymentAmount, apr: apr, paymentTooltipParams: paymentTooltipParams }) }) }), _jsx(Price, { children: priceComponent })] }));
    const ctaSection = (_jsx(Optional, { when: hasCtaSection, children: _jsxs(CtaSection, { isResponsive: ((_e = fields === null || fields === void 0 ? void 0 : fields.ctaLink1) === null || _e === void 0 ? void 0 : _e.href) && ((_f = fields === null || fields === void 0 ? void 0 : fields.ctaLink2) === null || _f === void 0 ? void 0 : _f.href), children: [_jsx(AccessoryModalCta, {}), _jsx(StandardCtas, { fields: fields, onClickHandler: onCtaClickHandler, isSummaryPage: isSummaryPage })] }) }));
    // Inventory sections
    const inventoryDetailsSection = (_jsxs(InventoryDetails, { children: [_jsxs(Box, { children: [_jsx(Badge, { status: inventoryStatus, styles: { mt: '0px', mb: 'xxs' } }), _jsx(H6, { "data-testid": "vehicle-name", children: compiler(stripMarkdownHeading(vehicleName)) })] }), priceComponent] }));
    const inventoryCtaSection = (_jsx(Optional, { when: hasCtaSection, children: _jsx(CtaSection, { isResponsive: ((_g = fields === null || fields === void 0 ? void 0 : fields.ctaLink1) === null || _g === void 0 ? void 0 : _g.href) && ((_h = fields === null || fields === void 0 ? void 0 : fields.ctaLink2) === null || _h === void 0 ? void 0 : _h.href), children: _jsx(StandardCtas, { fields: fields, onClickHandler: onCtaClickHandler, isInventoryVariant: true }) }) }));
    return (_jsxs(Container, { componentType: componentName, id: "bap-summary-bar", "data-testid": "cy-summary-bar", children: [_jsx(Wrapper, { children: variant === INVENTORY_VARIANT ? (_jsxs(_Fragment, { children: [inventoryDetailsSection, inventoryCtaSection] })) : (_jsxs(_Fragment, { children: [priceSection, ctaSection] })) }), _jsx(PaymentCalculatorModal, { originalCalculatorId: primaryCalculatorId }), _jsx(PaymentCompareModal, {}), _jsx(PriceBreakdownModal, {})] }));
};
export default SummaryBarUI;
